'use client';

import { Alert } from '../../../../components/alert';
import { Button } from '../../../../components/button';
import { Field } from '../../../../components/field';
import { GeneratedMailboxSection } from '../../../application-credentials/GeneratedMailboxSection';
import { Input } from '../../../../components/input';
import { RoleFormModal } from '../../../application-roles/RoleFormModal';
import { SegmentGroup } from '../../../../components/segment-group';
import { toasterApi } from '../../../toast/ToastProvider';
import { FieldWrapper } from '../../../../components/field-wrapper/FieldWrapper';
import { Tooltip } from '../../../../components/tooltip';
import { Box, HStack, token, VStack } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  InformationCircleIcon,
  Loading03Icon,
  MailboxIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { resolveCredentialMailboxAddress } from '../../credential-mailbox';
import {
  type CredentialLoginType,
  useAddCredentialsDialogStore,
} from '../AddCredentialsDialogStore';
import { PasswordField } from '../../../inputs/PasswordField';
import { CredentialInboxMessagesSection } from '../../credential-card/CredentialInboxMessagesSection';
import { RoleSelect } from '../components/RoleSelect';
import { MultiFactorAuthenticationStep } from './MultiFactorAuthenticationStep';
import { useShallow } from 'zustand/shallow';
import {
  type CreateCredentialRoleFn,
  useCredentialDataSources,
} from '../../CredentialDataSourcesContext';

type BasicInformationStepProps = {
  applicationId: string;
  hasGitIntegration: boolean;
  isEditing?: boolean;
};

export const BasicInformationStep = ({
  applicationId,
  hasGitIntegration,
  isEditing,
}: BasicInformationStepProps) => {
  const t = useTranslations(
    'designSystem.credentials.addDialog.steps.basicInformation',
  );
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const {
    credential,
    formMeta,
    setCredential,
    setFormMeta,
    validationErrors,
    setValidationErrors,
  } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      credential: state.credential,
      formMeta: state.formMeta,
      setCredential: state.setCredential,
      setFormMeta: state.setFormMeta,
      validationErrors: state.validationErrors,
      setValidationErrors: state.setValidationErrors,
    })),
  );
  const { generateMailbox, createRole } = useCredentialDataSources();
  const [mailboxJustSucceeded, setMailboxJustSucceeded] = useState(false);
  const [hasGeneratedMailbox, setHasGeneratedMailbox] = useState(false);
  const [isGeneratingMailbox, setIsGeneratingMailbox] = useState(false);
  const showGenerateMailboxError = useCallback(
    (error?: string) => {
      toasterApi.error({
        title: t('generateMailbox.errorTitle'),
        description: error ?? t('generateMailbox.errorDescription'),
      });
    },
    [t],
  );
  const handleGenerateMailbox = useCallback(async () => {
    if (!generateMailbox) {
      showGenerateMailboxError();

      return;
    }

    setMailboxJustSucceeded(false);
    setIsGeneratingMailbox(true);

    try {
      const response = await generateMailbox();

      if (!response.success || !response.emailAddress) {
        showGenerateMailboxError(response.error);

        return;
      }

      setFormMeta({ email: response.emailAddress });
      setCredential({
        generatedMailboxAddress: response.emailAddress,
        username: response.emailAddress,
      });
      setHasGeneratedMailbox(true);
      setMailboxJustSucceeded(true);
      setTimeout(() => setMailboxJustSucceeded(false), 2000);
    } catch (error) {
      console.error('Failed to generate mailbox:', error);
      showGenerateMailboxError();
    } finally {
      setIsGeneratingMailbox(false);
    }
  }, [generateMailbox, setFormMeta, setCredential, showGenerateMailboxError]);
  const handleCreateRole = useCallback(
    async (roleData: Parameters<CreateCredentialRoleFn>[0]) => {
      const role = createRole ? await createRole(roleData) : null;

      if (!role) {
        toasterApi.error({
          title: t('role.createError'),
        });

        return;
      }

      setCredential({ role: role.name, roleId: role.id });
      setValidationErrors({ role: '' });
    },
    [createRole, setCredential, setValidationErrors, t],
  );
  const loginTypeOptions = [
    { value: 'username', label: t('loginType.username') },
    { value: 'email', label: t('loginType.email') },
  ];
  const showEmail = formMeta.loginType === 'email';
  const generatedMailboxAddress = resolveCredentialMailboxAddress(credential);
  const mailboxAlreadyExists =
    Boolean(generatedMailboxAddress) || hasGeneratedMailbox;

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <FieldWrapper label={t('name.label')}>
        <Input
          size="sm"
          placeholder={t('name.placeholder')}
          value={credential.name ?? ''}
          onChange={(event) => setCredential({ name: event.target.value })}
        />
      </FieldWrapper>

      <FieldWrapper label={t('loginType.label')}>
        <SegmentGroup.Root
          value={formMeta.loginType}
          onValueChange={({ value }) =>
            setFormMeta({
              loginType: value as CredentialLoginType,
            })
          }
          bg="bg.neutral.subtle"
          borderRadius="2xl"
          borderWidth="2px"
          borderColor="bg.neutral.subtle"
        >
          <HStack gap={1} alignItems="stretch" position="relative" w="full">
            {loginTypeOptions.map((option) => (
              <SegmentGroup.Item
                key={option.value}
                value={option.value}
                flex={1}
                py="1"
                px="2"
                borderRadius="2xl"
                fontWeight="medium"
                fontSize="xs"
                color="fg.neutral.muted"
                alignItems="center"
                display="flex"
                justifyContent="center"
                _checked={{
                  color: 'fg.neutral.default',
                  bg: 'bg.neutral.muted',
                  boxShadow: 'sm',
                }}
              >
                <SegmentGroup.ItemControl display="none" />
                <SegmentGroup.ItemText>{option.label}</SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
            ))}
          </HStack>
        </SegmentGroup.Root>
      </FieldWrapper>

      {showEmail && !credential.id && (
        <Alert.Root
          bg="bg.vulnerability.cold"
          borderColor="border.vulnerability.cold"
          px="2"
          py="1.5"
          gap={2}
        >
          <Alert.Icon asChild>
            <InformationCircleIcon
              color={token('colors.fg.vulnerability.cold')}
              size={20}
            />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Description fontSize="sm" color="fg.neutral.default">
              {t('emailMfaAlert')}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      <FieldWrapper
        label={showEmail ? t('email.label') : t('username.label')}
        error={showEmail ? validationErrors.email : ''}
      >
        {showEmail ? (
          <HStack gap={2} alignItems="center" w="full">
            <Input
              flex={1}
              size="sm"
              placeholder={t('email.placeholder')}
              value={formMeta.email}
              onChange={(event) => {
                setFormMeta({ email: event.target.value });
                setCredential({ username: event.target.value });
                setValidationErrors({ email: '' });
              }}
            />
            <Tooltip
              title={
                mailboxJustSucceeded
                  ? t('generateMailbox.mailboxGenerated')
                  : mailboxAlreadyExists
                    ? t('generateMailbox.mailboxAlreadyGenerated')
                    : t('generateMailbox.tooltipTitle')
              }
              subtitle={
                mailboxJustSucceeded || mailboxAlreadyExists
                  ? undefined
                  : t('generateMailbox.tooltipDescription')
              }
              positioning={{ placement: 'top', strategy: 'fixed' }}
            >
              <Box as="span">
                <Button
                  aria-label={t('generateMailbox.ariaLabel')}
                  variant="outline"
                  size="sm"
                  disabled={
                    !generateMailbox ||
                    isGeneratingMailbox ||
                    mailboxAlreadyExists
                  }
                  onClick={handleGenerateMailbox}
                >
                  {isGeneratingMailbox ? (
                    <Box animation="spin 1s linear infinite" display="flex">
                      <Loading03Icon size={16} />
                    </Box>
                  ) : mailboxJustSucceeded ? (
                    <Tick01Icon size={16} />
                  ) : (
                    <MailboxIcon size={16} />
                  )}
                  {t('generateMailbox.ariaLabel')}
                </Button>
              </Box>
            </Tooltip>
          </HStack>
        ) : (
          <Input
            size="sm"
            placeholder={t('username.placeholder')}
            value={credential.username}
            onChange={(event) => {
              setCredential({ username: event.target.value });
            }}
          />
        )}
      </FieldWrapper>

      {showEmail && mailboxAlreadyExists && generatedMailboxAddress && (
        <GeneratedMailboxSection emailAddress={generatedMailboxAddress}>
          {credential.id ? (
            <CredentialInboxMessagesSection
              applicationId={applicationId}
              credentialId={credential.id}
            />
          ) : null}
        </GeneratedMailboxSection>
      )}

      <PasswordField
        label={t('password.label')}
        placeholder={t('password.placeholder')}
        value={credential.password}
        onChange={(value) => setCredential({ password: value })}
        hidePasswordToggle={isEditing}
      />

      <FieldWrapper
        label={t('role.label')}
        isRequired
        error={validationErrors.role}
      >
        <RoleSelect
          customRoles={formMeta.customRoles}
          selectedRole={credential.role}
          placeholder={t('role.placeholder')}
          createRoleLabel={t('role.create')}
          onRoleChange={(role) => {
            setCredential({ role });
            setValidationErrors({ role: '' });
          }}
          onCreateRoleClick={() => setIsRoleModalOpen(true)}
        />
      </FieldWrapper>

      <FieldWrapper label={t('roleDescription.label')}>
        <Field.Textarea
          placeholder={t('roleDescription.placeholder')}
          fontSize="sm"
          height="28"
          resize="none"
          value={credential.roleDescription ?? ''}
          onChange={(event) =>
            setCredential({
              roleDescription: event.target.value || null,
            })
          }
        />
      </FieldWrapper>

      <FieldWrapper label={t('tenant.label')} helperText={t('tenant.helper')}>
        <Input
          size="sm"
          placeholder={t('tenant.placeholder')}
          value={credential.tenant ?? ''}
          onChange={(event) =>
            setCredential({ tenant: event.target.value || null })
          }
        />
      </FieldWrapper>

      <MultiFactorAuthenticationStep />

      <RoleFormModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        applicationId={applicationId}
        hasGitIntegration={hasGitIntegration}
        onSave={handleCreateRole}
      />
    </VStack>
  );
};
